import React from 'react';
import {getServicesPaginationItems} from '../servicesPagination';
import styles from '../Services.module.css';

const iconUrl = (fileName: string) => `${process.env.PUBLIC_URL}/icons/${fileName}`;

interface ServicesPaginationProps {
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

const PaginationIcon: React.FC<{fileName: string}> = ({fileName}) => (
  <span
    className={styles.servicesPaginationIcon}
    style={{'--services-pagination-icon-url': `url("${iconUrl(fileName)}")`} as React.CSSProperties}
    aria-hidden="true"
  />
);

const ServicesPagination: React.FC<ServicesPaginationProps> = ({
  page,
  totalPages,
  onPageChange,
}) => {
  const paginationItems = getServicesPaginationItems(page, totalPages);
  const isPreviousDisabled = page <= 1;
  const isNextDisabled = totalPages <= 0 || page >= totalPages;

  if (totalPages <= 1) {
    return null;
  }

  return (
    <nav className={styles.servicesPagination} aria-label="Service cards pagination">
      <button
        type="button"
        className={styles.servicesPaginationButton}
        aria-label="Go to previous services page"
        title="Previous page"
        disabled={isPreviousDisabled}
        onClick={() => onPageChange(Math.max(1, page - 1))}
      >
        <PaginationIcon fileName="previous-ltr.svg" />
      </button>

      <div className={styles.servicesPaginationPages}>
        {paginationItems.map((item, index) => (
          item === 'ellipsis' ? (
            <span
              key={`ellipsis-${index}`}
              className={styles.servicesPaginationEllipsis}
              aria-hidden="true"
            >
              ...
            </span>
          ) : (
            <button
              key={item}
              type="button"
              className={`${styles.servicesPaginationPageButton} ${item === page ? styles.servicesPaginationPageButtonActive : ''}`.trim()}
              aria-label={`Go to page ${item}`}
              aria-current={item === page ? 'page' : undefined}
              onClick={() => onPageChange(item)}
            >
              {item}
            </button>
          )
        ))}
      </div>

      <button
        type="button"
        className={styles.servicesPaginationButton}
        aria-label="Go to next services page"
        title="Next page"
        disabled={isNextDisabled}
        onClick={() => onPageChange(Math.min(totalPages, page + 1))}
      >
        <PaginationIcon fileName="previous-rtl.svg" />
      </button>
    </nav>
  );
};

export default React.memo(ServicesPagination);
